// ── Canonical route paths ────────────────────────────────────────────────────
//
// Kept in their own module (rather than on Dashboard.js) so pages that Dashboard
// itself renders — WebsiteManagement, Tutorial, PlanSelector, … — can link to
// each other without importing their own parent and creating a module cycle.
//
// ── WHY PATHS AND NOT TAB INDICES ───────────────────────────────────────────
// Navigation between tabs used to be by NUMERIC INDEX into the Dashboard TABS
// array (`onTabChange(4)` for Settings). That array is built conditionally —
// the Analytics entry is spread in only when SHOW_ANALYTICS is true — so
// hiding Analytics silently shifted every index after it by one, and every
// hardcoded index started pointing at the wrong page:
//
//     with Analytics:  0 Overview 1 Websites 2 Credits 3 Analytics 4 Support …
//     without:         0 Overview 1 Websites 2 Credits 3 Support   4 Quick Start …
//
// That is the "Add Payment Method sends me to the Support tab" bug: the index
// was right when it was written and rotted when a tab was hidden. It moved
// again (to Quick Start Guide) when Tutorial was renamed.
//
// Paths are stable under insertion, removal and reordering, so this class of
// bug cannot recur. Nothing outside Dashboard.js should reference a tab index.

export const OVERVIEW_PATH = '/overview';

/** The Websites tab — where API Key and Site Code live. */
export const WEBSITES_PATH = '/websites';

export const CREDITS_PATH = '/credits';

/**
 * Credits, opened directly on the plans/subscriptions screen.
 * CreditManagement reads `?view=plans` (see its showPlans state).
 */
export const PLANS_PATH = '/credits?view=plans';

/**
 * The plans screen, scrolled to the three paid cards.
 *
 * REQUESTED by "Get subscription" in the after-the-trial dialog: land on
 * Manage Plan and go DOWN to the plans. Plain PLANS_PATH opens at the top of
 * the page, where the options card explains the two ways to buy — which is the
 * right landing for someone still choosing, and the wrong one for someone who
 * has just chosen in a dialog that said the same thing.
 *
 * A query param rather than a hash: the screen is already a query-derived view
 * (`?view=plans`), a hash would fight the router's scroll restoration, and this
 * way the whole intent survives a refresh as one URL. PlanSelector reads it —
 * see its `scrollTo` prop.
 */
export const PLANS_SCROLL_TARGET = 'plans';
export const PLANS_SCROLLED_PATH = `${PLANS_PATH}&scrollTo=${PLANS_SCROLL_TARGET}`;

export const SUPPORT_PATH = '/support';

/** Support, with the new-ticket dialog already open. */
export const NEW_TICKET_PATH = '/support?newTicket=1';

/**
 * Settings — this is where the Payment Methods section lives, so it is the
 * correct destination for every "add / choose a card" prompt.
 */
export const SETTINGS_PATH = '/settings';

/**
 * The in-app, authenticated "Quick Start Guide" page.
 * Renamed from "Tutorial"; App.js redirects the old /tutorial path here.
 */
export const QUICK_START_PATH = '/quick-start-guide';

/**
 * The separate, no-login tutorials page. Mounted OUTSIDE the auth guard in
 * App.js so it can be shared publicly; renders the same content as the
 * Quick Start Guide tab.
 */
export const PUBLIC_TUTORIALS_PATH = '/tutorials';

/**
 * The Quick Start Guide, opened at "How credits are used".
 *
 * Named here rather than written inline at the call site for the same reason
 * every other path is: the anchor and the section id have to agree, and a
 * hardcoded '#how-credits-are-used' is exactly the kind of link that rots
 * silently when the section is renamed.
 *
 * The ANCHOR is what Tutorial.jsx stamps on the section. The PATH has no
 * caller at present — the credits tooltip that used it now opens the breakdown
 * panel on its own card instead, which answers the question about the reader's
 * own credits rather than about credits in general. It is kept as the one
 * correct way to deep-link that section.
 */
export const CREDITS_GUIDE_ANCHOR = 'how-credits-are-used';
export const CREDITS_GUIDE_PATH = `${QUICK_START_PATH}#${CREDITS_GUIDE_ANCHOR}`;

/**
 * Credits, with the credit-pack picker already open.
 *
 * Same shape as NEW_TICKET_PATH above, and for the same reason: the pack
 * picker is a dialog held in CreditManagement's local state, so a plain link
 * to /credits would land the user on the balance card and leave them to find
 * the Add Credits button themselves. Settings links here when telling a
 * free-plan account how to get credits without subscribing.
 */
export const BUY_CREDITS_PATH = '/credits?addCredits=1';
